import { z } from 'zod';

import {
  briefPlacementSchema,
  campaignBriefSchema,
  type BriefGapField,
  type BriefPlacement,
  type BriefPlatformPlan,
  type CampaignBrief,
} from './campaign-brief.js';
import { creativeFormatIdSchema, MAX_VARIANTS, type CreativeFormatId } from './creative.js';
import { BRAND_MOCKUP_ITEMS, BRAND_MOCKUP_MAX_ITEMS, BRAND_MOCKUP_STYLES, type BrandMockupItem, type BrandMockupStyle } from './brand-mockups.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * TASLAK KART — ajanın doldurduğu SAYFA FORMU (19 Eyl 2026)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Kullanıcının isteği, kendi sözleriyle: *"adam dicek ki ben kreatif üretmek
 * istiyorum işte şu ürünüme… o otomatik doldurcak o sayfada gibi düşün. eksik
 * varsa varsay, yanlış bilgi varsa söyleyecek"*.
 *
 * Onay kartı bu yüzden bir ÖZET SATIRI olmaktan çıktı ve sayfanın formunun
 * sohbetteki hâli oldu:
 *
 *   · Kreatif kartı stüdyonun alanlarını taşıyor (ürün, tür, şablon, manken,
 *     format, adet, yönerge) ve bedeli canlı hesaplıyor.
 *   · Kampanya kartı `/campaigns/new` brief'inin KENDİSİ: aynı şema
 *     (`campaignBriefSchema`), aynı derleyici (`planBrief`), aynı eksik alan
 *     listesi (`briefGaps`). İkinci bir kampanya formu YOK.
 *
 * Kullanıcı kartın içinde düzeltiyor (`PATCH /agent/proposals/:id`), sunucu
 * aynı doğrulamadan geçirip kartı tazeliyor, sonra onay. Model bir sonraki
 * turda neyin değiştiğini öğreniyor (`AGENT_CARDS_BLOCK_TYPE`).
 *
 * ---------------------------------------------------------------------------
 * 🔴 KONTROLLER SUNUCUDA, TEK KAYNAKTA
 * ---------------------------------------------------------------------------
 * "Eksik varsa söyle, yanlışsa söyle" iki katmanda:
 *
 *   1. MODELİN CÜMLESİ — neyi varsaydığını ve neyin kulağa yanlış geldiğini
 *      söylüyor (sistem prompt'u). Bu, bağlam isteyen yargı.
 *   2. KARTIN KONTROL LİSTESİ (`DraftCheck`) — kural olan her şey: görselsiz
 *      ürün, reklam metninde fiyat, Meta'nın reddettiği vaat, eksik platform
 *      alanı, yetmeyen kredi. Bunlar modelin hatırlamasına bırakılmıyor.
 *
 * `block` seviyesi onay düğmesini KAPATIYOR — çünkü o hâlde onay ya 4xx
 * dönecek ya da platformda yarım bir nesne bırakacaktı. `warn` açık bırakıyor
 * (karar kullanıcının), `info` yalnız söylüyor.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Kontroller
// ═══════════════════════════════════════════════════════════════════════════

export const DRAFT_CHECK = {
  /// Ürünün fotoğrafı yok — üretim ürün görselinden yola çıkıyor.
  PRODUCT_NO_IMAGE: 'product.noImage',
  /// Ürünün herkese açık adresi yok — reklam bir yere bağlanamaz.
  PRODUCT_NO_URL: 'product.noUrl',
  /// Kredi bakiyesi bu üretime yetmiyor.
  CREDITS_SHORT: 'credits.short',
  /// Reklam metninde fiyat var — pazaryeri kuponu fiyatı değiştiriyor.
  COPY_PRICE: 'copy.price',
  /// Reklam metninde tedavi/garanti vaadi — Meta reddediyor, hesaba işliyor.
  COPY_CLAIM: 'copy.claim',
  /// Başlık Meta'nın kırptığı sınırı (~40) aşıyor.
  HEADLINE_LONG: 'copy.headlineLong',
  /// Gövde ~125 karakterden sonra katlanıyor; ilk cümle mesajı taşımalı.
  BODY_LONG: 'copy.bodyLong',
  /// Platformun zorunlu alanı cevaplanmadı (Meta Sayfası, Google kelimeleri…).
  GAP_MISSING: 'gap.missing',
  /// Hiç platform seçilmedi.
  NO_PLACEMENT: 'placement.none',
  /// Seçilen amaç bu platformda yok / adapter bu platformda kuramıyor.
  PLACEMENT_BLOCKED: 'placement.blocked',
  /// Payına düşen günlük bütçe çok küçük.
  BUDGET_SHARE_LOW: 'budget.shareLow',
  /// Platform payları toplamı toplam bütçeyi aşıyor.
  BUDGET_SPREAD_OVER: 'budget.spreadOver',
  /// Google görselleri kullanmıyor — reklam salt metin.
  GOOGLE_TEXT_ONLY: 'google.textOnly',
  /// Pazaryeri sayfası piksel taşımıyor; satış/potansiyel müşteri ölçülemez.
  MARKETPLACE_PIXEL: 'goal.marketplacePixel',
  /// Bitiş günü yok — kampanya durdurulana kadar harcar.
  NO_END_DATE: 'budget.noEndDate',
  /// Mankenli çekim bir manken ister.
  PRESENTER_REQUIRED: 'presenter.required',
  /**
   * UGC + ürün fotoğrafı (2 Eki 2026): fotoğraf konuşturulmuyor — ürünü elinde
   * tutan bir sunucu üretilip o konuşturuluyor; açıklama sahne tarifi değil
   * konuşma maddesi. Bilgi; kullanıcı beceriyi değiştirdiğinde ne alacağını
   * görsün diye (olayda görsel kartı UGC'ye çevrilmişti).
   */
  UGC_PRESENTER_FROM_PRODUCT: 'ugc.presenterFromProduct',
} as const;
export type DraftCheckCode = (typeof DRAFT_CHECK)[keyof typeof DRAFT_CHECK];

export interface DraftCheck {
  code: DraftCheckCode;
  /**
   * `block` → onay KAPALI (yapılsaydı ya reddedilecek ya yarım kalacaktı).
   * `warn`  → onay açık, kullanıcı bilerek geçiyor.
   * `info`  → bilgi.
   */
  level: 'block' | 'warn' | 'info';
  /// Kartta hangi alanın yanına düşeceği — `null` kartın geneli.
  field: string | null;
  /// Platforma özgü kontrollerde hangi satır.
  adAccountId?: string | null;
  platform?: string | null;
  /// Çeviri parametreleri (`{count}`, `{limit}`…).
  params?: Record<string, string | number>;
}

/**
 * REKLAM METNİ DENETİMİ — fiyat ve vaat.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden sözlük, neden SAF
 * ═══════════════════════════════════════════════════════════════════════════
 * İki kural da ürünün eski kararları (`create_campaign` şemasındaki "NEVER
 * put a price", "never make a treatment or guarantee claim") ve bugüne kadar
 * yalnız MODELE söyleniyordu. Kullanıcı kartta metni kendisi değiştirebildiği
 * an kural modelden bağımsız uygulanmak zorunda: kart düzenlenmiş metni de
 * denetliyor. Saf olması tarayıcıda da koşabilmesi için — kart yazarken
 * uyarıyı sunucu turu beklemeden gösterebiliyor.
 *
 * ⚠️ Sözlük BİLEREK dar. Geniş bir liste ("en iyi", "harika") her metni
 * işaretler ve kullanıcı uyarıyı okumamayı öğrenir. Buradakiler Meta'nın
 * reklam politikasında adı geçen sınıflar: tedavi, garanti, kesin sonuç.
 */
const PRICE_PATTERN =
  /(?:₺|\$|€|£)\s?\d|\d[\d.,]*\s?(?:₺|tl|try|usd|eur|gbp|lira)\b|\b(?:fiyat[ıi]?|price)\s*[:=]?\s*\d/i;

const CLAIM_PATTERN = new RegExp(
  [
    'garanti',
    'guarantee',
    /* "%100 pamuk" bir malzeme bilgisi, vaat değil — yalnız sonuç/doğallık
       iddiasıyla birleşen yüzde işaretleniyor. */
    '%\\s?100\\s?(?:doğal|etkili|sonuç|başarı|garanti)',
    '100\\s?%\\s?(?:natural|effective|guaranteed|results?)',
    'kesin sonuç',
    'kesin çözüm',
    'tedavi',
    'iyileştirir',
    'iyileştiren',
    'dökülmeyi durdur',
    'dökülmesini durdur',
    'cure[sd]?\\b',
    'heals?\\b',
    'stops? hair loss',
    'miracle',
    'mucize',
  ].join('|'),
  'i',
);

export function lintAdCopy(text: string | null | undefined): DraftCheckCode[] {
  if (!text) return [];
  const out: DraftCheckCode[] = [];
  if (PRICE_PATTERN.test(text)) out.push(DRAFT_CHECK.COPY_PRICE);
  if (CLAIM_PATTERN.test(text)) out.push(DRAFT_CHECK.COPY_CLAIM);
  return out;
}

/// Meta feed başlığı ~40 karakterden sonra kırpıyor (`campaignBriefSchema` başlığı).
export const AD_HEADLINE_SOFT_LIMIT = 40;
/// Gövde ~125 karakterden sonra "devamı" ile katlanıyor.
export const AD_BODY_SOFT_LIMIT = 125;

// ═══════════════════════════════════════════════════════════════════════════
// Kreatif taslağı
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Kreatif kartının çizim modeli — `proposal.after.draft`.
 *
 * `request` (üretime gidecek tam gövde) de `after`ta duruyor ama o bir
 * `CreativeGenerateInput`: şablon kimliği, iç içe `scene`/`video` nesneleri.
 * Kart onu okuyup adları ve fiyatı yeniden türetmek zorunda kalmasın diye
 * sunucu okunur bir görünüm de yazıyor. İkisi AYNI çözümden doğuyor
 * (`resolveCreativeRequest`) ve düzenlemede birlikte yeniden yazılıyor.
 */
export interface AgentCreativeDraft {
  skill: string;
  kind: 'image' | 'video';
  brief: string;
  product: {
    productId: string;
    title: string;
    imageUrl: string | null;
    platform: string;
  } | null;
  template: { id: string; name: string } | null;
  presenterId: string | null;
  /// Görselde format; videoda `null` (oran şablondan).
  formatId: CreativeFormatId | null;
  video: { aspectRatio: string; durationSeconds: number } | null;
  variants: number;
  unitCredits: number;
  credits: number;
  availableCredits: number;
  /// Modelin VARSAYIM olarak doldurduğu alanlar — kart onları işaretliyor.
  assumed: string[];
  checks: DraftCheck[];
  /**
   * Taban/logo SOHBET EKİNDEN geldiyse (Faz 8 — `sourceAttachmentId` /
   * `logoAttachmentId`): kart "ürün yok" yerine eki gösterir. Anahtar değil
   * yalnız ek kimliği (`a1`).
   */
  source?: { attachmentId: string | null; logoAttachmentId: string | null } | null;
  /**
   * MARKA MOCKUP SETİ (4 Eki 2026, `brand-mockup`) — kartın "Logo" ve
   * "Mockuplar" satırları. Taban ürün değil logo: sohbet ekinden geldiyse
   * `attachmentId` (balondaki çiple eşleşiyor), kütüphanedeyse `logoId` + ad
   * + kısa ömürlü önizleme adresi. Öteki becerilerde alan YOK.
   */
  mockup?: {
    items: BrandMockupItem[];
    style: BrandMockupStyle;
    logo: {
      attachmentId: string | null;
      logoId: string | null;
      name: string | null;
      previewUrl: string | null;
    };
  } | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// Kampanya taslağı
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Kampanya kartının çizim modeli — `proposal.after.draft`.
 *
 * `brief` + `placements` onay anında `CampaignBriefService.launch`a AYNEN
 * giden gövde (`briefLaunchSchema`). `plans` son adımdaki platform satırları:
 * çalışma alanının BÜTÜN hesapları, seçilmemiş olanlar da — kullanıcı kartta
 * bir platform ekleyebilsin ya da çıkarabilsin.
 */
export interface AgentCampaignDraft {
  brief: CampaignBrief;
  placements: BriefPlacement[];
  plans: BriefPlatformPlan[];
  product: {
    productId: string;
    title: string;
    imageUrl: string | null;
    platform: string;
  } | null;
  creatives: {
    creativeId: string;
    name: string | null;
    type: string;
    width: number | null;
    height: number | null;
  }[];
  /// Seçili yerleşimlerde cevaplanmamış zorunlu alanlar.
  missing: { adAccountId: string; fields: BriefGapField[] }[];
  assumed: string[];
  checks: DraftCheck[];
}

// ═══════════════════════════════════════════════════════════════════════════
// Düzenleme — `PATCH /agent/proposals/:id`
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Karttan gelen düzeltme.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden araç başına AYRI dal
 * ═══════════════════════════════════════════════════════════════════════════
 * Her kartın düzenlenebilir alanları farklı ve kapalı: bütçe kartında yalnız
 * yeni bütçe, katalog kartında yalnız metinler. Serbest bir `patch` nesnesi
 * kabul etmek, kartın göstermediği bir alanı (ör. kampanya kimliği) istemcinin
 * değiştirebilmesi demekti.
 *
 * Kampanya dalı brief'i ve yerleşimleri BÜTÜN OLARAK alıyor, alan alan değil:
 * yerleşim payları toplam bütçeye, eksik alanlar amaca bağlı ve kısmi bir
 * yama bu bağları sunucuda yeniden kurmayı gerektirirdi. Kart zaten tam
 * hâli elinde tutuyor.
 */
export const proposalEditSchema = z.discriminatedUnion('tool', [
  z.object({
    tool: z.literal('adjust_budget'),
    dailyBudget: z.number().positive().max(1_000_000),
  }),
  z.object({
    tool: z.literal('generate_creative'),
    /// Tür değişirse şablon ve format DÜŞÜYOR — görsel şablonu videoya
    /// uymaz; reçete yeni türün varsayılanını kuruyor.
    skill: z.string().trim().min(1).max(40).optional(),
    brief: z.string().trim().min(10).max(2000).optional(),
    formatId: creativeFormatIdSchema.optional(),
    variants: z.number().int().min(1).max(MAX_VARIANTS).optional(),
    /// `null` şablonu kaldırıyor — yönergeden serbest sahne.
    templateId: z.string().trim().max(80).nullable().optional(),
    presenterId: z.string().trim().max(64).nullable().optional(),
    /**
     * Marka mockup seti (4 Eki 2026) — kartın "Mockuplar" çipleri ve stil
     * seçicisi. Yalnız `brand-mockup` becerisinde anlamlı; varyant sayısı
     * sunucuda öğe sayısına eşitleniyor (`variants` bu beceride yok sayılır).
     */
    mockups: z
      .array(z.enum(BRAND_MOCKUP_ITEMS))
      .min(1)
      .max(BRAND_MOCKUP_MAX_ITEMS)
      .optional(),
    mockupStyle: z.enum(BRAND_MOCKUP_STYLES).optional(),
  }),
  z.object({
    tool: z.literal('fix_product_content'),
    title: z.string().trim().min(3).max(255).optional(),
    description: z.string().trim().min(20).max(5000).optional(),
    productType: z.string().trim().min(2).max(120).optional(),
    tags: z.array(z.string().trim().min(1).max(60)).max(30).optional(),
  }),
  z.object({
    tool: z.literal('plan_campaign'),
    brief: campaignBriefSchema,
    placements: z.array(briefPlacementSchema).max(10),
  }),
  /**
   * Katalog operasyonu kartı — parametrelerin TAMAMI yeniden gönderiliyor.
   *
   * Şema burada GENEL (`record`): her operasyonun kendi Zod şeması var ve
   * sunucu düzenlemeyi o şemadan (`PlatformOpsService.parseParams`) ve
   * gerekiyorsa platform doğrulamasından (`dryRun`) geçiriyor. Kart da o
   * şemayı `GET /platform-ops/catalog/:opId` → `inputSchema`dan çiziyor.
   */
  z.object({
    tool: z.literal('platform_op'),
    /// Operasyon parametrelerinin TAMAMI, girdi biçiminde (`AgentPlatformOpDraft.input`).
    input: z.record(z.string(), z.unknown()),
  }),
  /**
   * İŞLEM PLANI kartı (Faz 8 dalga 2, kampanya planı görünümü) — değişen
   * ADIMLARIN girdisi, adım başına TAMAMI. Sunucu her adımı kendi şemasından
   * (`parsePlanStep`) ve gerekiyorsa platform doğrulamasından geçiriyor;
   * adımlar arası referanslar (`$1.created[0].platformRef`) DEĞİŞTİRİLEMEZ,
   * mühürlü (kişisel veri) adım ve müşteri listesi planı düzenlenemez.
   */
  z.object({
    tool: z.literal('platform_op_plan'),
    steps: z
      .array(
        z.object({
          /* 25 = `OPERATION_PLAN_LIMITS.maxSteps` — sabit aşağıda tanımlı ve
             şema modül yüklenirken kuruluyor (TDZ); sayı elle, eşitliği
             `agent-plan-amend.spec.ts` kilitliyor. */
          index: z.number().int().min(1).max(25),
          input: z.record(z.string(), z.unknown()),
        }),
      )
      .min(1)
      .max(25),
  }),
]);
export type ProposalEditInput = z.infer<typeof proposalEditSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Katalog operasyonu kartı (29 Eyl 2026)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * PLATFORM OPERASYON KATALOĞU kartı — `proposal.after.draft`
 * (`tool === 'platform_op'`).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden TEK bir genel kart
 * ═══════════════════════════════════════════════════════════════════════════
 * Katalog beş platformda yüzlerce operasyon taşıyor (`@mentorivo/shared/
 * platform-ops`). Her birine ayrı bir kart yazmak mümkün değil; kart başlığı,
 * platformu, düzeyi, harcama etkisi ve parametreleri operasyonun kendisinden
 * geliyor. Parametre alanlarının etiketleri ve tipleri için arayüz
 * `GET /platform-ops/catalog/:opId` → `inputSchema` okuyor.
 *
 * `policy` otomatik pilot kararının DÖKÜMÜ: kart "bu işlem otomatik
 * uygulanmadı çünkü bütçe artışı %25 tavanını aşıyor" diyebilsin. Kararın
 * kendisi sunucuda, `decideOp` (saf) ile verildi.
 *
 * `validation` platformun kendi doğrulaması (`dryRun` — Google `validate_only`,
 * Meta `execution_options`): kart onaya düşmeden platforma sorulur. Platform
 * reddettiyse kart HİÇ DOĞMAZ (hata modele döner); buradaki `rejected` yalnız
 * düzenleme sonrası tekrar doğrulamada görülür.
 */
export interface AgentPlatformOpDraft {
  opId: string;
  /** Operasyonun kart başlığı (`PlatformOpDef.title`). */
  title: { en: string; tr: string };
  platform: string;
  /** `OpLevel` — CAMPAIGN, ADSET, AD, KEYWORD, AUDIENCE… */
  level: string;
  /** Operasyonun koşacağı reklam hesabı (bizim `AdAccount.id`). */
  adAccountId: string;
  adAccountName: string | null;
  currency: string | null;
  /** Parametrelerden çözülmüş harcama etkisi (`OpImpact`). */
  impact: 'NONE' | 'DECREASE' | 'INCREASE' | 'NEW_SPEND';
  /** Başka bir operasyonla geri alınabilir mi; `false` = silme/kalıcı kaldırma. */
  reversible: boolean;
  /**
   * Operasyona GİDEN parametreler — Zod'un GİRDİ biçimi (modelin yazdığı,
   * doğrulamadan geçmiş hâl). Onayda AYNEN bunlar koşar; kart düzenlemesi de
   * bu biçimde (`inputSchema` `io: 'input'` ile üretiliyor).
   */
  input: Record<string, unknown>;
  /**
   * Doğrulanmış (Zod `output`) hâl — varsayılanlar doldurulmuş, dönüşümler
   * uygulanmış. Kartta GÖSTERİM ve politika hesabı bunu okur.
   */
  params: Record<string, unknown>;
  /** Modelin kısa gerekçesi (varsa) — kartın altında. */
  reason: string | null;
  /** Platformun istediği erişim notu (`requires`) — reddedilirse ne yapılacağı. */
  requires: { access?: string; scopes?: readonly string[]; note?: string } | null;
  policy: {
    verdict: 'AUTO' | 'APPROVAL';
    /** `OpPolicyReason` — `SUGGEST_MODE`, `BUDGET_CHANGE_TOO_LARGE`, … */
    reasons: string[];
    facts: {
      budgetChangePercent?: number;
      dailySpendAdded?: number;
      createsCampaign?: boolean;
      /** Faz 7 — FUNDS: tavanın biriminde ölçülen taşınan tutar (ANA birim) ve birimi. */
      fundsMoved?: number;
      fundsCurrency?: string;
    };
    /** Reklam hesabı para birimi ≠ çalışma alanı → harcama tavanı karşılaştırılamadı. */
    currencyMismatch?: boolean;
  };
  validation:
    | { status: 'passed'; summary: string | null; warnings: string[] }
    | { status: 'skipped'; reason: 'DRY_RUN_UNSUPPORTED' }
    | { status: 'rejected'; code: string; message: string };
  /** Onaydan sonra: platformun sonucu (oluşan/değişen nesneler). */
  result?: {
    summary: string;
    created?: { level: string; platformRef: string; name?: string; status?: string }[];
    changed?: { level: string; platformRef: string }[];
    warnings?: string[];
  } | null;
  /**
   * Hassas işlem sınıfı (`resolveSensitivity(def, params)`, Faz 4 — 29 Eyl 2026):
   * erişim/yetki, varış adresi, veri paylaşımı ya da yasal beyan. Doluysa
   * otomatik pilot HER ZAMAN onaya düşürür (`SENSITIVE_OP`); kart rozeti bunu
   * okur. Eski kartlarda alan yok.
   */
  sensitivity?: import('./platform-ops/types.js').OpSensitivity | null;
  /**
   * 🔴 KİŞİSEL VERİ MÜHÜRÜ (KVKK/GDPR, 29 Eyl 2026). Müşteri listesi gibi
   * kişisel veri taşıyan üst düzey parametreler (`fields`) `input`/`params`
   * içinde GİZLİ yer tutucuyla (`"[redacted: N items]"`) saklanır; ham değer
   * şifreli ve SÜRELİ (onay süresi) bir kasada durur ve yalnız onayda
   * yürütücüye verilir. Kart bu alanları DÜZENLENEMEZ gösterir; yer tutucuyu
   * değiştirmeden geri gönderen düzenleme ham değeri korur.
   */
  sealed?: { ref: string; fields: string[]; expiresAt: string } | null;
  /**
   * Onaydan sonra UYGULANAMADIYSA sebebi (Faz 5, 29 Eyl 2026): kod + sanitize
   * edilmiş mesaj (platformun kendi cümlesi olabilir). `AgentProposal.error`
   * bununla aynı; kartın kendi verisinde de duruyor.
   */
  failure?: { code: string; message: string } | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// İŞLEM PLANI kartı (Faz 5, 29 Eyl 2026)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * İşlem planı sınırları. Adım tavanı modelin "her şeyi tek kartta" isteğiyle
 * kullanıcının kartı okuyabilmesi arasındaki denge; referans derinliği ve
 * çözülen değer boyutu JSONPath-lite'ın güvenlik sınırı.
 */
export const OPERATION_PLAN_LIMITS = {
  maxSteps: 25,
  /** `$1.created[0].platformRef` gibi yolun en fazla parça sayısı. */
  maxRefSegments: 8,
  /** Çözülen referans değerinin JSON boyutu tavanı (karakter). */
  maxResolvedChars: 20_000,
  /** Referans aranan parametre ağacının en derin seviyesi. */
  maxParamDepth: 10,
} as const;

/**
 * Plan adımı — ajan aracının ve REST ucunun ORTAK girdisi.
 *
 * `params` içinde bir değer ÖNCEKİ bir adımın sonucuna başvurabilir:
 *   · `"$1.created[0].platformRef"`              — dize biçimi
 *   · `{ "$ref": "1.created[0].platformRef" }`    — nesne biçimi (tek anahtar)
 *   · `"$2.created[level=ADSET].platformRef"`     — düzeye göre seçici
 * Kök `created` / `changed` / `data` / `summary`; adım numarası 1'den başlar ve
 * YALNIZ ÖNCEKİ adımı gösterebilir. Değerlendirme YOK (eval yok): yol sabit
 * bir dil bilgisiyle ayrıştırılıyor (`apps/api/src/platform-ops/operation-plan.ts`).
 */
export const operationPlanStepSchema = z.object({
  opId: z.string().trim().min(3).max(120),
  params: z.record(z.string(), z.unknown()).default({}),
  /** Kartta adımın kısa etiketi ("Kampanya", "Reklam seti A"). */
  label: z.string().trim().max(120).optional(),
});
export type OperationPlanStepInput = z.input<typeof operationPlanStepSchema>;

/** `platform_op_plan` ajan aracının girdisi. */
export const operationPlanInputSchema = z.object({
  /** Kart başlığı — kullanıcının dilinde, ne kurulduğunu söyler. */
  title: z.string().trim().min(3).max(160),
  /** Modelin kısa gerekçesi — kartın altında. */
  reason: z.string().trim().max(1000).optional(),
  steps: z.array(operationPlanStepSchema).min(1).max(OPERATION_PLAN_LIMITS.maxSteps),
});
export type OperationPlanInput = z.input<typeof operationPlanInputSchema>;

/** Plan adımının doğrulama durumu. */
export type PlanStepValidation =
  | { status: 'passed'; summary: string | null; warnings: string[] }
  | { status: 'skipped'; reason: 'DRY_RUN_UNSUPPORTED' | 'DEPENDS_ON_PREVIOUS' }
  | { status: 'rejected'; code: string; message: string };

/** Plan adımının onay sonrası sonucu. */
export interface PlanStepResult {
  /** `not_run`: önceki bir adım düştüğü için hiç denenmedi. */
  status: 'applied' | 'failed' | 'not_run';
  summary?: string;
  created?: { level: string; platformRef: string; name?: string; status?: string }[];
  changed?: { level: string; platformRef: string }[];
  warnings?: string[];
  error?: { code: string; message: string } | null;
}

export interface AgentPlatformOpPlanStep {
  /** 1'den başlayan sıra — referanslar (`$1…`) bununla. */
  index: number;
  opId: string;
  title: { en: string; tr: string };
  label: string | null;
  platform: string;
  level: string;
  adAccountId: string;
  adAccountName: string | null;
  currency: string | null;
  impact: 'NONE' | 'DECREASE' | 'INCREASE' | 'NEW_SPEND';
  reversible: boolean;
  sensitivity: import('./platform-ops/types.js').OpSensitivity | null;
  requires: { access?: string; scopes?: readonly string[]; note?: string } | null;
  /** Girdi biçimi — referanslar YER TUTUCU olarak duruyor (`$1…`). Onayda bu koşar. */
  input: Record<string, unknown>;
  /**
   * Gösterim + politika hâli (Zod `output`). Referanslı alanlarda referansın
   * kendisi yazılı (değer onayda çözülüyor).
   */
  params: Record<string, unknown>;
  /** Bu adımın referansları: parametre yolu → kaynak (`1.created[0].platformRef`). */
  refs: { path: string; ref: string }[];
  /** Başvurduğu önceki adımlar (1'den). */
  dependsOn: number[];
  /** Adımın tek başına politika kararı — kartın toplam kararı `policy`de. */
  policy: {
    verdict: 'AUTO' | 'APPROVAL';
    reasons: string[];
    facts: {
      budgetChangePercent?: number;
      dailySpendAdded?: number;
      createsCampaign?: boolean;
      /** Faz 7 — FUNDS adımının taşıdığı tutar (tavanın biriminde) ve birimi. */
      fundsMoved?: number;
      fundsCurrency?: string;
    };
  };
  validation: PlanStepValidation;
  /** Kişisel veri mührü — `AgentPlatformOpDraft.sealed` ile aynı kural, adım başına. */
  sealed?: { ref: string; fields: string[]; expiresAt: string } | null;
  result?: PlanStepResult | null;
}

/**
 * İŞLEM PLANI kartı — `proposal.after.draft` (`tool === 'platform_op_plan'`).
 *
 * Tek onay, sıralı yürütme: adımlar `PlatformOpsService.run` ile sırayla koşar
 * (idempotency `proposal:<id>:<adım>`), referanslar bir önceki sonuçlardan
 * çözülür, İLK hatada durulur. Kısmi başarı mümkün: düşen adımdan önce
 * oluşanlar platformda kalır ve kimlikleri `steps[].result.created`da.
 *
 * Politika TÜM adımlar için: her adım `decideOp`tan geçer, kart ancak HEPSİ
 * otomatikse otomatik uygulanır; sebepler birleşir, harcama toplanır,
 * kampanya yayına almaları sayılır.
 */
export interface AgentPlatformOpPlanDraft {
  /** `plan` = modelin kurduğu zincir; `customer_audience` = mağaza/lead listesi aracı. */
  kind: 'plan' | 'customer_audience';
  title: string;
  reason: string | null;
  steps: AgentPlatformOpPlanStep[];
  platforms: string[];
  /** En yüksek etki (NONE < DECREASE < INCREASE < NEW_SPEND). */
  impact: 'NONE' | 'DECREASE' | 'INCREASE' | 'NEW_SPEND';
  /** Adımlardan biri hassassa onun sınıfı (öncelik: ACCESS > DATA_SHARING > …). */
  sensitivity: import('./platform-ops/types.js').OpSensitivity | null;
  /** Bütün adımlar geri alınabilir mi. */
  reversible: boolean;
  policy: {
    verdict: 'AUTO' | 'APPROVAL';
    /** Adımların sebeplerinin birleşimi (ilk görülme sırası). */
    reasons: string[];
    facts: {
      /** En büyük adım artışı (%). */
      budgetChangePercent?: number;
      /** Adımların eklediği günlük harcama TOPLAMI. */
      dailySpendAdded?: number;
      createsCampaign?: boolean;
      /** Planın yayına aldığı kampanya sayısı (yeni kampanya kotası). */
      campaignLaunches?: number;
      /** Yazma adımı sayısı (günlük aksiyon kotası). */
      actions?: number;
      /** Faz 7 — adımların taşıdığı para TOPLAMI (tavanın biriminde) ve birimi. */
      fundsMoved?: number;
      fundsCurrency?: string;
    };
    currencyMismatch?: boolean;
  };
  /**
   * Müşteri listesi aracının kaynağı — YALNIZ sayılar (kişisel veri değil).
   * `consent`: mağaza pazarlama iznini taşıyor muydu, yoksa kullanıcı açıkça
   * mı onayladı.
   */
  source?: {
    kind: 'store' | 'leads' | 'file';
    platform: string;
    total: number;
    included: number;
    excludedNoConsent: number;
    excludedNoIdentifier: number;
    consent: 'MARKETING_CONSENT' | 'USER_CONFIRMED';
    withEmail: number;
    withPhone: number;
  } | null;
  /** Onaydan sonra: tamamlanan adım sayısı ve (varsa) düşen adım. */
  completedSteps?: number;
  failedStep?: number | null;
  /**
   * Arka plan yürütmesinin durumu (Faz 5) — onay isteği planı BEKLEMİYOR:
   * kart `applying` olur, iş (`QUEUE.PLATFORM_OP_PLAN`) adımları sırayla
   * koşar ve her adımdan sonra kartı günceller.
   *   `queued`  kuyrukta · `running` adım koşuyor (`currentStep`) ·
   *   `done`    bitti (sonuç `status`ta: applied / failed)
   */
  run?: {
    state: 'queued' | 'running' | 'done';
    currentStep: number | null;
    queuedAt: string;
    startedAt: string | null;
    finishedAt: string | null;
    /** Kaçıncı deneme (çökme sonrası devam 2+). */
    attempts: number;
  } | null;
  /** Plan düştüyse düşen adımın hatası (`AgentProposal.error` ile aynı). */
  failure?: { code: string; message: string } | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// Müşteri listesi kitlesi (`build_customer_audience`, Faz 5)
// ═══════════════════════════════════════════════════════════════════════════

export const CUSTOMER_AUDIENCE_PLATFORMS = ['META', 'GOOGLE', 'TIKTOK', 'LINKEDIN', 'TWITTER'] as const;
export type CustomerAudiencePlatform = (typeof CUSTOMER_AUDIENCE_PLATFORMS)[number];

export const CUSTOMER_AUDIENCE_LIMITS = {
  /** Tek kartta taşınan en fazla kişi (izin süzgecinden sonra). */
  maxPeople: 50_000,
  /** Lead kaynağında en fazla seçilen lead kimliği. */
  maxLeadIds: 5_000,
} as const;

/**
 * `build_customer_audience` aracının girdisi.
 *
 * 🔴 KİŞİSEL VERİ modele HİÇ gitmiyor: araç müşterileri (mağazadan) ya da
 * lead'leri (platformun lead okuma operasyonundan) KENDİSİ okuyor, platform
 * kuralına göre normalize + SHA-256'lıyor ve bir İŞLEM PLANI kartı
 * sahneliyor (mühürlü). Modele yalnız sayılar dönüyor. Kart HER ZAMAN insan
 * onayı istiyor (`DATA_SHARING`).
 */
export const customerAudienceInputSchema = z
  .object({
    source: z.discriminatedUnion('kind', [
      z.object({
        kind: z.literal('store'),
        /** Bağlı mağaza platformu (SHOPIFY, WOOCOMMERCE, IKAS…); tek mağaza varsa gerekmez. */
        storePlatform: z.string().trim().min(2).max(40).optional(),
      }),
      z.object({
        kind: z.literal('leads'),
        platform: z.enum(CUSTOMER_AUDIENCE_PLATFORMS),
        /** Platformun lead okuma operasyonunun (`<platform>.lead.list`) parametreleri. */
        params: z.record(z.string(), z.unknown()).default({}),
        /** Yalnız bu lead'ler (nitelikli bulunanlar). Verilmezse okunan hepsi. */
        leadIds: z
          .array(z.string().trim().min(1).max(200))
          .max(CUSTOMER_AUDIENCE_LIMITS.maxLeadIds)
          .optional(),
      }),
      /**
       * KULLANICININ YÜKLEDİĞİ MÜŞTERİ LİSTESİ (CSV) — Faz 8, 30 Eyl 2026.
       *
       * Liste sohbete ek olarak geldi (`[Attachment a3: customer list CSV …]`)
       * ve şifreli, süreli kasada duruyor (`POST /agent/customer-lists`).
       * Model yalnız ekin KİMLİĞİNİ yazıyor; sunucu kasayı açıp kişileri
       * bellekte okuyor. Pazarlama izni kaydı YOK → `userConfirmedConsent` şart.
       */
      z.object({
        kind: z.literal('file'),
        /** Sohbetteki müşteri listesi ekinin kimliği (`a3`). */
        attachmentId: z
          .string()
          .trim()
          .regex(/^a[1-9]\d{0,3}$/, 'The customer list attachment id, like "a3".'),
      }),
    ]),
    targets: z
      .array(
        z.object({
          platform: z.enum(CUSTOMER_AUDIENCE_PLATFORMS),
          /** Mevcut liste/segment — verilirse yeni liste AÇILMAZ, kişiler ona eklenir. */
          audienceRef: z.string().trim().min(1).max(300).optional(),
        }),
      )
      .min(1)
      .max(CUSTOMER_AUDIENCE_PLATFORMS.length),
    /** Yeni listenin adı (hedef platformlarda aynı). */
    audienceName: z.string().trim().min(1).max(120),
    /**
     * Kaynak pazarlama iznini TAŞIMIYORSA (WooCommerce, lead formu) kullanıcı
     * "bu kişilerin reklam için kullanılmasına izin var" diye AÇIKÇA
     * onaylamalı. Model bunu kendisi `true` yapamaz — kullanıcıya sorar.
     */
    userConfirmedConsent: z.boolean().default(false),
    /** Google Customer Match için yasal beyan (AEA) — yalnız kullanıcının cevabıyla. */
    googleConsent: z
      .object({
        adUserData: z.enum(['GRANTED', 'DENIED']),
        adPersonalization: z.enum(['GRANTED', 'DENIED']),
      })
      .optional(),
    /** Yeni listeden benzer kitle (Meta, TikTok, Google) — ayrı son adım. */
    lookalike: z
      .object({
        countries: z.array(z.string().trim().length(2)).min(1).max(20),
        /** Meta oranı (0.01 = %1). TikTok/Google boyuta çevriliyor. */
        ratio: z.number().min(0.01).max(0.2).default(0.01),
      })
      .optional(),
    /** Ülke kodsuz telefonlar için ülke telefon kodu ("90"). Verilmezse o numaralar atlanır. */
    defaultCallingCode: z
      .string()
      .trim()
      .regex(/^\d{1,4}$/)
      .optional(),
  })
  .refine((v) => new Set(v.targets.map((t) => t.platform)).size === v.targets.length, {
    message: 'Each target platform once',
    path: ['targets'],
  });
export type CustomerAudienceInput = z.input<typeof customerAudienceInputSchema>;

/// Hangi araçların kartı düzenlenebilir — arayüz bu listeye bakıp alanları açıyor.
export const EDITABLE_PROPOSAL_TOOLS = [
  'adjust_budget',
  'generate_creative',
  'fix_product_content',
  'plan_campaign',
  /* Faz 8 dalga 2: kampanya planı kartı adım parametrelerini düzenletiyor
     (müşteri listesi planı ve mühürlü adım sunucuda reddediliyor). */
  'platform_op_plan',
] as const;
